import clsx from 'clsx';
import { AlarmClock, CircleCheck, CircleDashed, TriangleAlert } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NONE, TASK_STAGES, type AnalysisConfig, type AnalysisDim, type GroupStat, type ScheduleState } from '@pz/core';
import type { ProcOutput } from '@pz/server';
import { num, pct, pctRange } from '../../lib/format';

export type AnalysisData = ProcOutput<'analysis.project'>;

/**
 * Colours of the analysis (see styles.css): progress (finished share) uses one green ramp in five steps (0-20 %, ...,
 * 80-100 %; round 4: an own scale, apart from the blue of the application), hours use the blue ramp, the schedule
 * state uses the status colours, always with icon and label. Groups without tasks are hatched.
 */
export function progStep(p: number): 0 | 1 | 2 | 3 | 4 {
  if (!Number.isFinite(p) || p <= 0) return 0;
  return Math.min(4, Math.floor(p * 5)) as 0 | 1 | 2 | 3 | 4;
}
export const progFill = (p: number) => `var(--done-${progStep(p)})`;
export const progInk = (p: number) => `var(--done-ink-${progStep(p)})`;
/** Hours relative to the largest value (building in "hours" mode, hours per person): the blue ramp. */
export const hoursFill = (p: number) => `var(--prog-${progStep(p)})`;
export const hoursInk = (p: number) => `var(--prog-ink-${progStep(p)})`;

/** Percent or "-" for groups without a value. */
export const pctOr = (x: number, decimals = 0) => (Number.isFinite(x) ? pct(x, decimals) : '-');

export function ProgressLegend({ className }: { className?: string }) {
  const { t } = useTranslation();
  const steps = [0, 20, 40, 60, 80].map((from) => pctRange(from, from + 20));
  return (
    <div className={clsx('flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-graphite', className)}>
      <span>{t('analysis.legendProgress')}</span>
      {steps.map((s, i) => (
        <span key={s} className="flex items-center gap-1">
          <span className="inline-block h-3 w-4 rounded-[3px]" style={{ background: `var(--done-${i})` }} />
          <span className="num">{s}</span>
        </span>
      ))}
      <span className="flex items-center gap-1">
        <span className="hatch inline-block h-3 w-4 rounded-[3px] border border-line" />
        {t('analysis.noTasks')}
      </span>
    </div>
  );
}

/** Legend of the hours ramp (share of the largest value). */
export function HoursLegend({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={clsx('flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-graphite', className)}>
      <span>{t('analysis.legendHours')}</span>
      <span>{t('analysis.legendLess')}</span>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className="inline-block h-3 w-4 rounded-[3px]" style={{ background: `var(--prog-${i})` }} />
      ))}
      <span>{t('analysis.legendMore')}</span>
    </div>
  );
}

const SCHEDULE: Record<ScheduleState, { tone: string; soft: string; icon: ReactNode }> = {
  ok: { tone: 'text-good', soft: 'bg-good-soft', icon: <CircleCheck className="size-3.5" aria-hidden /> },
  risk: { tone: 'text-warn', soft: 'bg-warn-soft', icon: <TriangleAlert className="size-3.5" aria-hidden /> },
  late: { tone: 'text-bad', soft: 'bg-bad-soft', icon: <AlarmClock className="size-3.5" aria-hidden /> },
  none: { tone: 'text-graphite', soft: 'bg-sheet-2', icon: <CircleDashed className="size-3.5" aria-hidden /> },
};

export const scheduleSoft = (s: ScheduleState) => SCHEDULE[s].soft;
export const scheduleTone = (s: ScheduleState) => SCHEDULE[s].tone;
export const scheduleIcon = (s: ScheduleState) => SCHEDULE[s].icon;

/** Schedule state with icon and label (never colour alone). */
export function ScheduleBadge({ state, className, title }: { state: ScheduleState; className?: string; title?: string }) {
  const { t } = useTranslation();
  return (
    <span title={title ?? t(`analysis.scheduleHint.${state}`)} className={clsx('inline-flex items-center gap-1 rounded-full px-2 py-px text-xs font-medium whitespace-nowrap', SCHEDULE[state].soft, SCHEDULE[state].tone, className)}>
      {SCHEDULE[state].icon}
      {t(`analysis.schedule.${state}`)}
    </span>
  );
}

export function ScheduleLegend({ className }: { className?: string }) {
  return (
    <div className={clsx('flex flex-wrap items-center gap-2', className)}>
      {(['ok', 'risk', 'late', 'none'] as const).map((s) => (
        <ScheduleBadge key={s} state={s} />
      ))}
    </div>
  );
}

/** The four stages as an ordinal ramp: open (outlined), in progress, in review, finished. */
export const STAGE_FILL: Record<(typeof TASK_STAGES)[number], string> = {
  open: 'var(--sheet-2)',
  progress: 'var(--done-1)',
  review: 'var(--done-2)',
  done: 'var(--done-4)',
};

export function StageLegend({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={clsx('flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-graphite', className)}>
      {TASK_STAGES.map((s) => (
        <span key={s} className="flex items-center gap-1">
          <span className={clsx('inline-block h-3 w-4 rounded-[3px]', s === 'open' && 'border border-line-strong')} style={{ background: STAGE_FILL[s] }} />
          {t(`analysis.stages.${s}`)}
        </span>
      ))}
    </div>
  );
}

/** Stacked bar of the four stages of a group (100 % wide), 2 px gaps between the parts. */
export function StageBar({ s, className }: { s: Pick<GroupStat, 'open' | 'progress' | 'review' | 'done' | 'tasks'>; className?: string }) {
  if (!s.tasks) return <span className={clsx('hatch block h-3 rounded-[3px] border border-line', className)} aria-hidden />;
  const parts = (['done', 'review', 'progress', 'open'] as const).filter((k) => s[k] > 0);
  return (
    <span className={clsx('flex h-3 gap-0.5', className)} aria-hidden>
      {parts.map((k, i) => (
        <span
          key={k}
          className={clsx(k === 'open' && 'border border-line-strong', i === 0 && 'rounded-l-[3px]', i === parts.length - 1 && 'rounded-r-[3px]')}
          style={{ flex: `${s[k]} 1 0`, background: STAGE_FILL[k] }}
        />
      ))}
    </span>
  );
}

export interface DimInfo {
  key: AnalysisDim;
  label: string;
}

/**
 * Dimensions of a project for filters and groupings: building, floor, trade and work stage (as configured), the
 * Forma category and type, the stage, the other custom fields taken over, and the person (hours).
 */
export function useDims(data: AnalysisData | undefined) {
  const { t } = useTranslation();
  return useMemo(() => {
    const fields = new Map((data?.fields ?? []).map((f) => [f.id, f.title]));
    const config: AnalysisConfig | undefined = data?.config;
    const sourceLabel = (dim: 'building' | 'floor' | 'zone') => {
      const s = config?.[dim];
      if (!s || s.kind === 'none') return t(`analysis.dims.${dim}`);
      if (s.kind === 'attr') return fields.get(s.id) ?? t(`analysis.dims.${dim}`);
      return t(`analysis.dims.${dim}`);
    };
    const used = new Set<string>();
    const list: DimInfo[] = [];
    if (config && config.building.kind !== 'none') {
      list.push({ key: 'building', label: sourceLabel('building') });
      if (config.building.kind === 'attr') used.add(config.building.id);
    }
    if (config && config.floor.kind !== 'none') {
      list.push({ key: 'floor', label: sourceLabel('floor') });
      if (config.floor.kind === 'attr') used.add(config.floor.id);
    }
    // zones of a floor: third tier of the Forma locations (round 5, request 13)
    if (config?.zone && config.zone.kind !== 'none') {
      list.push({ key: 'zone', label: sourceLabel('zone') });
      if (config.zone.kind === 'attr') used.add(config.zone.id);
    }
    if (config?.trade) {
      list.push({ key: 'trade', label: fields.get(config.trade) ?? t('analysis.dims.trade') });
      used.add(config.trade);
    }
    if (config?.phase) {
      list.push({ key: 'phase', label: fields.get(config.phase) ?? t('analysis.dims.phase') });
      used.add(config.phase);
    }
    list.push({ key: 'category', label: t('analysis.dims.category') });
    list.push({ key: 'type', label: t('analysis.dims.type') });
    // a custom field with the same name as a dimension (e.g. the field "Geschoss" next to the floors of the Forma locations)
    // is marked as a Forma field, so two filters never carry the same label (round 5, request 13)
    const taken = new Set(list.map((d) => d.label.toLocaleLowerCase()));
    for (const f of data?.fields ?? []) {
      if (used.has(f.id)) continue;
      list.push({ key: `attr:${f.id}`, label: taken.has(f.title.toLocaleLowerCase()) ? `${f.title} (${t('analysis.dims.formaField')})` : f.title });
    }
    list.push({ key: 'stage', label: t('analysis.dims.stage') });
    list.push({ key: 'person', label: t('analysis.dims.person') });
    const persons = new Map((data?.persons ?? []).map((p) => [p.id, p.name]));
    const label = (dim: AnalysisDim) => list.find((d) => d.key === dim)?.label ?? dim;
    const valueLabel = (dim: AnalysisDim, v: string) => {
      if (v === NONE || v === '') return t('analysis.none');
      if (dim === 'person') return persons.get(v) ?? v;
      if (dim === 'stage') return t(`analysis.stages.${v as 'open'}`);
      return v;
    };
    return { list, label, valueLabel, personName: (id: string) => persons.get(id) ?? id };
  }, [data, t]);
}

/** Small figure tile of the analysis. */
export function Tile({ label, value, hint, tone, icon }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: 'good' | 'warn' | 'bad'; icon?: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-lg border border-line bg-sheet px-3 py-2.5 print-avoid-break">
      <span className="flex items-center gap-1 text-xs text-graphite">
        {icon}
        {label}
      </span>
      <span className={clsx('num text-xl font-semibold', tone === 'bad' && 'text-bad', tone === 'warn' && 'text-warn', tone === 'good' && 'text-good')}>{value}</span>
      {hint && <span className="text-xs text-graphite">{hint}</span>}
    </div>
  );
}

/** "12 / 30" with the share */
export function countText(done: number, total: number) {
  return `${num(done)} / ${num(total)}`;
}
